import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Activity as ActivityIcon, ArrowRight, Banknote, Clock, Coins, FileSearch, ImageUp, Inbox, PiggyBank, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ActivityRow, ReviewActions } from '@/components/admin';
import { PageHeader } from '@/components/layout/AppShell';
import { PurchaseRow } from '@/components/lists';
import { AnimatedNumber, Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import { cn, formatMoney } from '@/lib/utils';
import { ListSkeleton } from '../ChecksPage';

function Stat({
  label,
  value,
  icon: Icon,
  tint,
  sub,
  to,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  tint: string;
  sub?: string;
  to?: string;
}) {
  const body = (
    <Card className={cn('group relative h-full overflow-hidden p-5 transition-all', to && 'hover:-translate-y-0.5 hover:shadow-glow')}>
      <div className={cn('absolute -right-8 -top-8 size-28 rounded-full opacity-30 blur-2xl transition-transform group-hover:scale-125', tint)} />
      <div className="relative flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className={cn('flex size-9 items-center justify-center rounded-xl text-white', tint)}>
          <Icon className="size-4" />
        </span>
      </div>
      <p className="relative mt-3 text-3xl font-black tracking-tight">{value}</p>
      {sub && <p className="relative mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function AdminOverviewPage() {
  const stats = useQuery({ queryKey: ['admin', 'stats'], queryFn: adminApi.stats, refetchInterval: 30_000 });
  const pending = useQuery({
    queryKey: ['admin', 'purchases', { status: 'pending', limit: 5 }],
    queryFn: () => adminApi.purchases({ status: 'pending', limit: 5 }),
    refetchInterval: 30_000,
  });
  const activity = useQuery({ queryKey: ['admin', 'activity', { limit: 10 }], queryFn: () => adminApi.activity({ limit: 10 }), refetchInterval: 30_000 });
  const s = stats.data;

  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Overview" description="Sales, coins and checks at a glance. Refreshes every 30 seconds." />

      {stats.isError ? (
        <ErrorState error={stats.error} onRetry={() => stats.refetch()} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {!s
            ? Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)
            : (
              [
                { label: 'Pending receipts', value: <AnimatedNumber value={s.pending_purchases} />, icon: Inbox, tint: 'bg-rose-500', to: '/admin/purchases', sub: s.pending_purchases ? 'Waiting for review' : 'All caught up' },
                { label: 'Revenue', value: formatMoney(s.revenue), icon: Banknote, tint: 'bg-emerald-500', sub: s.currency },
                { label: 'Coins sold', value: <AnimatedNumber value={s.coins_sold} />, icon: Coins, tint: 'bg-amber-500', sub: `${s.approved_purchases} approved purchases` },
                { label: 'Coins in balances', value: <AnimatedNumber value={s.coins_in_balances} />, icon: PiggyBank, tint: 'bg-sky-500', sub: 'Not yet spent' },
                { label: 'Users', value: <AnimatedNumber value={s.users} />, icon: Users, tint: 'bg-slate-800', to: '/admin/users' },
                { label: 'Checks (total)', value: <AnimatedNumber value={s.checks_total} />, icon: FileSearch, tint: 'bg-yellow-500', to: '/admin/checks' },
                { label: 'Checks (24 h)', value: <AnimatedNumber value={s.checks_last_24h} />, icon: Clock, tint: 'bg-slate-500' },
                { label: 'Pending screenshots', value: <AnimatedNumber value={s.pending_screenshots} />, icon: ImageUp, tint: 'bg-orange-500', to: '/admin/screenshots', sub: s.pending_screenshots ? 'Failed checks to read by hand' : 'All caught up' },
              ].map((x, i) => (
                <motion.div key={x.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Stat {...x} />
                </motion.div>
              ))
            )}
        </div>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <Rise>
          <Card className="overflow-hidden">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <Inbox className="size-4 text-rose-500" /> Review queue
              </CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin/purchases">
                  Open queue <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            <div className="divide-y border-t">
              {pending.isPending ? (
                <ListSkeleton rows={3} />
              ) : pending.isError ? (
                <ErrorState error={pending.error} onRetry={() => pending.refetch()} />
              ) : pending.data.length === 0 ? (
                <EmptyState icon={<Inbox />} title="No receipts waiting" description="New coin purchases will show up here." />
              ) : (
                pending.data.map((p) => <PurchaseRow key={p.id} purchase={p} showUser actions={<ReviewActions purchase={p} />} />)
              )}
            </div>
          </Card>
        </Rise>
        <Rise>
          <Card className="overflow-hidden">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <ActivityIcon className="size-4 text-primary-ink" /> Recent activity
              </CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin/activity">
                  All <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            <div className="divide-y border-t">
              {activity.isPending ? (
                <ListSkeleton rows={4} />
              ) : activity.isError ? (
                <ErrorState error={activity.error} onRetry={() => activity.refetch()} />
              ) : activity.data.length === 0 ? (
                <EmptyState icon={<ActivityIcon />} title="Nothing yet" />
              ) : (
                activity.data.map((a) => <ActivityRow key={a.id} a={a} />)
              )}
            </div>
          </Card>
        </Rise>
      </div>
    </Page>
  );
}
